/**
 * Feedback Parser Module
 * Utilities for parsing and managing feedback data from local folders
 */

/**
 * Feedback configuration for different processing modes
 */
export const FEEDBACK_QUESTIONS = {
    teethml: [
        "Is the tooth segmentation accurate?",
        "Are the tooth boundaries clearly defined?",
        "Would you use this result in clinical practice?"
    ],
    defectml: [
        "Are the defects correctly identified?",
        "Is the defect classification accurate?",
        "Are there any missed defects?"
    ],
    keypointml: [
        "Are the keypoints correctly positioned?",
        "Are all expected keypoints detected?",
        "Is the keypoint classification accurate?"
    ],
    arrangement: [
        "Is the teeth arrangement clinically acceptable?",
        "Are the occlusal contacts properly established?",
        "Would you approve this arrangement plan?"
    ],
    teethseparation: [
        "Is the tooth separation accurate?",
        "Are the individual teeth properly reconstructed?",
        "Is the gingival line preservation adequate?"
    ]
};

/**
 * Processing mode display names
 */
export const PROCESSING_MODE_NAMES = {
    teethml: "Teeth Segmentation",
    defectml: "Defect Detection",
    keypointml: "Keypoint Detection",
    arrangement: "Teeth Arrangement",
    teethseparation: "Teeth Reconstruction"
};

/**
 * Parse feedback data from a collection of files
 * @param {File[]} files - Array of files from the feedback folder
 * @returns {Promise<Object[]>} Array of parsed feedback items
 */
export async function parseFeedbackFiles(files) {
    const feedbackGroups = groupFilesByFeedbackId(files);
    const parsedFeedback = [];

    for (const [id, fileGroup] of feedbackGroups) {
        try {
            const feedbackItem = await parseFeedbackItem(id, fileGroup);
            parsedFeedback.push(feedbackItem);
        } catch (error) {
            console.warn(`Failed to parse feedback ${id}:`, error);
            // Add a basic item with error info
            parsedFeedback.push({
                id,
                error: error.message,
                files: fileGroup,
                processingMode: "unknown",
                date: null,
                valid: false
            });
        }
    }

    return parsedFeedback.sort(compareFeedbackItems);
}

/**
 * Group files by their feedback ID (parent folder name)
 * @param {File[]} files - Array of files
 * @returns {Map<string, File[]>} Map of feedback ID to files
 */
function groupFilesByFeedbackId(files) {
    const groups = new Map();

    for (const file of files) {
        const pathParts = file.webkitRelativePath.split("/");
        if (pathParts.length < 2) continue;

        // Extract feedback ID from the path structure
        // Handle multiple path patterns:
        // 1. feedback/ml-feedback/[feedbackId]/files... (when selecting parent feedback dir)
        // 2. [feedbackId]/files... (when selecting ml-feedback dir directly)
        // 3. ml-feedback/[feedbackId]/files... (when selecting feedback dir with ml-feedback)
        let feedbackId = null;

        if (
            pathParts.length >= 3 &&
            pathParts[0] === "feedback" &&
            pathParts[1] === "ml-feedback"
        ) {
            feedbackId = pathParts[2]; // feedback/ml-feedback/[id]/...
        } else if (pathParts.length >= 2 && pathParts[0] === "ml-feedback") {
            feedbackId = pathParts[1]; // ml-feedback/[id]/...
        } else if (pathParts.length >= 1) {
            // When selecting ml-feedback directory directly, the first part is the feedback ID
            feedbackId = pathParts[0]; // [id]/...
        }

        if (!feedbackId || feedbackId === "ml-feedback") continue; // Skip if no ID or if it's the ml-feedback folder itself

        if (!groups.has(feedbackId)) {
            groups.set(feedbackId, []);
        }
        groups.get(feedbackId).push(file);
    }

    console.log(
        `📋 Grouped files into ${groups.size} feedback items:`,
        Array.from(groups.keys())
    );
    return groups;
}

/**
 * Parse an individual feedback item from its files
 * @param {string} id - Feedback ID
 * @param {File[]} files - Files associated with this feedback
 * @returns {Promise<Object>} Parsed feedback item
 */
async function parseFeedbackItem(id, files) {
    const feedbackItem = {
        id,
        files,
        valid: true,
        error: null,

        // Basic info
        processingMode: null,
        doctorName: null,
        rating: null,
        date: null,

        // Files
        meshFiles: [],
        resultFiles: [],
        formDataFile: null,

        // Parsed data
        formData: null,
        predictionData: null,
        responses: {},
        comments: null,
        systemInfo: {},
        postprocessing: null
    };

    // Categorize files
    categorizeFeedbackFiles(files, feedbackItem);

    // Parse form data if available
    if (feedbackItem.formDataFile) {
        await parseFormData(feedbackItem);
    }

    // Parse prediction results if available
    await parsePredictionResults(feedbackItem);

    // Validate the feedback item
    validateFeedbackItem(feedbackItem);

    return feedbackItem;
}

/**
 * Categorize feedback files by their type and purpose
 * @param {File[]} files - Files to categorize
 * @param {Object} feedbackItem - Feedback item to populate
 */
function categorizeFeedbackFiles(files, feedbackItem) {
    console.log(
        `🔍 Categorizing ${files.length} files for feedback ${feedbackItem.id}:`
    );

    for (const file of files) {
        const fileName = file.name.toLowerCase();
        const fileExt = fileName.split(".").pop();

        console.log(`  📄 File: ${file.name} (${fileName})`);

        if (
            fileName === "feedback.json" ||
            fileName === "form_data.json" ||
            fileName === "feedback_complete.json"
        ) {
            feedbackItem.formDataFile = file;
            console.log(`    ✅ Set as form data file`);
        } else if (fileExt === "obj" || fileExt === "stl") {
            feedbackItem.meshFiles.push(file);
            console.log(`    ✅ Added to mesh files`);
        } else if (
            fileExt === "json" &&
            (fileName.includes("result") || fileName.includes("prediction"))
        ) {
            feedbackItem.resultFiles.push(file);
            console.log(`    ✅ Added to result files`);
        } else {
            console.log(`    ❌ Not categorized`);
        }
    }

    console.log(`📋 Categorization result for ${feedbackItem.id}:`, {
        formDataFile: feedbackItem.formDataFile?.name || "None",
        meshFiles: feedbackItem.meshFiles.length,
        resultFiles: feedbackItem.resultFiles.length
    });
}

/**
 * Parse form data from the feedback JSON file
 * @param {Object} feedbackItem - Feedback item to populate
 */
async function parseFormData(feedbackItem) {
    try {
        console.log(
            `📄 Parsing form data for ${feedbackItem.id}:`,
            feedbackItem.formDataFile?.name
        );

        if (!feedbackItem.formDataFile) {
            throw new Error("No form data file available");
        }

        const text = await readFileAsText(feedbackItem.formDataFile);
        console.log(`📖 Read ${text.length} characters from form data file`);

        const data = JSON.parse(text);
        console.log(`✅ Successfully parsed JSON data:`, Object.keys(data));

        feedbackItem.formData = data;

        // Handle different feedback file formats
        let formData, feedbackData;

        if (data.feedback_data && data.feedback_data.formData) {
            // feedback_complete.json format
            feedbackData = data.feedback_data;
            formData = feedbackData.formData;

            feedbackItem.processingMode =
                formData.processingMode || feedbackData.processingMode;
            feedbackItem.doctorName =
                formData.doctorName || formData.doctor_name;
            feedbackItem.rating = formData.rating;
            feedbackItem.comments =
                formData.additionalComments || formData.comments;

            // Handle dynamic questions from feedback_complete.json
            if (formData.dynamicQuestions) {
                feedbackItem.responses = formData.dynamicQuestions;
            }

            // Extract date from multiple possible locations
            if (data.submission_time) {
                feedbackItem.date = new Date(data.submission_time);
            } else if (feedbackData.timestamp) {
                feedbackItem.date = new Date(feedbackData.timestamp);
            } else if (feedbackData.submissionTime) {
                feedbackItem.date = new Date(feedbackData.submissionTime);
            }

            // Extract postprocessing parameters for arrangement mode
            if (feedbackItem.processingMode === "arrangement") {
                if (feedbackData.files?.postprocessing) {
                    feedbackItem.postprocessing =
                        feedbackData.files.postprocessing;
                } else if (feedbackData.postprocessing) {
                    feedbackItem.postprocessing = feedbackData.postprocessing;
                }
            }
        } else {
            // Legacy feedback.json or form_data.json format
            feedbackItem.processingMode =
                data.processing_mode || data.processingMode;
            feedbackItem.doctorName = data.doctor_name || data.doctorName;
            feedbackItem.rating = data.rating;
            feedbackItem.comments = data.comments;
            feedbackItem.systemInfo = data.system_info || data.systemInfo || {};

            // Parse responses
            if (data.responses || data.question_responses) {
                const responses = data.responses || data.question_responses;
                if (typeof responses === "object") {
                    feedbackItem.responses = responses;
                } else if (typeof responses === "string") {
                    try {
                        feedbackItem.responses = JSON.parse(responses);
                    } catch (e) {
                        console.warn("Failed to parse responses:", e);
                        feedbackItem.responses = {};
                    }
                }
            }

            // Extract date
            if (data.timestamp) {
                feedbackItem.date = new Date(data.timestamp);
            } else if (data.date) {
                feedbackItem.date = new Date(data.date);
            }

            // Extract postprocessing parameters for arrangement mode (legacy format)
            if (feedbackItem.processingMode === "arrangement") {
                if (data.files?.postprocessing) {
                    feedbackItem.postprocessing = data.files.postprocessing;
                } else if (data.postprocessing) {
                    feedbackItem.postprocessing = data.postprocessing;
                }
            }
        }

        console.log(`Parsed feedback for ${feedbackItem.id}:`, {
            processingMode: feedbackItem.processingMode,
            doctorName: feedbackItem.doctorName,
            rating: feedbackItem.rating,
            date: feedbackItem.date
        });
    } catch (error) {
        console.warn(
            `Failed to parse form data for ${feedbackItem.id}:`,
            error
        );
        feedbackItem.error = `Form data parsing error: ${error.message}`;
    }
}

/**
 * Parse prediction results from result JSON files
 * @param {Object} feedbackItem - Feedback item to populate
 */
async function parsePredictionResults(feedbackItem) {
    for (const resultFile of feedbackItem.resultFiles) {
        try {
            const text = await readFileAsText(resultFile);
            const data = JSON.parse(text);

            // Store the first valid result file as the main prediction data
            if (!feedbackItem.predictionData) {
                feedbackItem.predictionData = data;
            }
        } catch (error) {
            console.warn(
                `Failed to parse result file ${resultFile.name}:`,
                error
            );
        }
    }
}

/**
 * Validate a feedback item for completeness and correctness
 * @param {Object} feedbackItem - Feedback item to validate
 */
function validateFeedbackItem(feedbackItem) {
    const issues = [];

    // Check for essential components
    if (!feedbackItem.formData) {
        issues.push("No form data found");
    }

    // Check for mesh files with fallback logic
    if (feedbackItem.meshFiles.length === 0) {
        // Try to find mesh files from different sources
        let foundMeshFiles = false;

        // Check if we can infer mesh files from file names or other sources
        if (feedbackItem.files && feedbackItem.files.length > 0) {
            const potentialMeshFiles = feedbackItem.files.filter(
                (file) =>
                    file.name.toLowerCase().endsWith(".obj") ||
                    file.name.toLowerCase().endsWith(".stl")
            );

            if (potentialMeshFiles.length > 0) {
                console.log(
                    `🔄 Found ${potentialMeshFiles.length} potential mesh files during validation`
                );
                feedbackItem.meshFiles = potentialMeshFiles;
                foundMeshFiles = true;
            }
        }

        if (!foundMeshFiles) {
            // This is now a warning rather than an error for some feedback types
            console.warn(
                `⚠️ No mesh files found for feedback ${feedbackItem.id}`
            );
        }
    }

    // Check processing mode with fallback logic
    if (!feedbackItem.processingMode) {
        // Try to infer processing mode from available data
        let inferredMode = null;

        // Check if we have arrangement-like data
        if (feedbackItem.predictionData?.arrangement_result) {
            inferredMode = "arrangement";
        }
        // Check file names for arrangement patterns
        else if (
            feedbackItem.meshFiles.some(
                (file) =>
                    file.name.toLowerCase().includes("upper") ||
                    file.name.toLowerCase().includes("lower") ||
                    file.name.toLowerCase().includes("mandibular") ||
                    file.name.toLowerCase().includes("maxillary")
            )
        ) {
            inferredMode = "arrangement";
        }
        // Default fallback
        else if (feedbackItem.meshFiles.length > 0) {
            inferredMode = "segmentation"; // Default mode
        }

        if (inferredMode) {
            console.log(
                `🔍 Inferred processing mode '${inferredMode}' for feedback ${feedbackItem.id}`
            );
            feedbackItem.processingMode = inferredMode;
        } else {
            issues.push("Processing mode not specified");
        }
    }

    if (!feedbackItem.date) {
        // Use file modification date as fallback
        const fileDates = feedbackItem.files
            .map((f) => f.lastModified)
            .filter((d) => d);
        if (fileDates.length > 0) {
            feedbackItem.date = new Date(Math.min(...fileDates));
        } else {
            issues.push("No date information available");
        }
    }

    // Set validity - be more lenient with validation
    // Only mark as invalid if critical issues exist
    const criticalIssues = issues.filter(
        (issue) =>
            issue.includes("No form data found") ||
            issue.includes("Processing mode not specified")
    );

    feedbackItem.valid = criticalIssues.length === 0;
    if (issues.length > 0) {
        feedbackItem.error = issues.join("; ");
        if (feedbackItem.valid) {
            console.warn(
                `⚠️ Feedback ${feedbackItem.id} has warnings: ${feedbackItem.error}`
            );
        }
    }
}

/**
 * Compare two feedback items for sorting
 * @param {Object} a - First feedback item
 * @param {Object} b - Second feedback item
 * @returns {number} Comparison result
 */
function compareFeedbackItems(a, b) {
    // Sort valid items before invalid ones
    if (a.valid !== b.valid) {
        return a.valid ? -1 : 1;
    }

    // Sort by date (newest first)
    if (a.date && b.date) {
        return b.date - a.date;
    }

    // Sort by ID as fallback
    return a.id.localeCompare(b.id);
}

/**
 * Read a file as text
 * @param {File} file - File to read
 * @returns {Promise<string>} File contents as text
 */
function readFileAsText(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
    });
}

/**
 * Format a processing mode for display
 * @param {string} mode - Processing mode key
 * @returns {string} Formatted display name
 */
export function formatProcessingMode(mode) {
    return PROCESSING_MODE_NAMES[mode] || mode || "Unknown";
}

/**
 * Get questions for a processing mode
 * @param {string} mode - Processing mode key
 * @returns {string[]} Array of questions
 */
export function getQuestionsForMode(mode) {
    return FEEDBACK_QUESTIONS[mode] || [];
}

/**
 * Extract key statistics from feedback data
 * @param {Object[]} feedbackData - Array of feedback items
 * @returns {Object} Statistics object
 */
export function calculateFeedbackStatistics(feedbackData) {
    const stats = {
        total: feedbackData.length,
        valid: 0,
        invalid: 0,
        processingModes: {},
        averageRating: 0,
        dateRange: { earliest: null, latest: null }
    };

    let ratingSum = 0;
    let ratingCount = 0;

    for (const item of feedbackData) {
        if (item.valid) {
            stats.valid++;
        } else {
            stats.invalid++;
        }

        // Count processing modes
        const mode = item.processingMode || "unknown";
        stats.processingModes[mode] = (stats.processingModes[mode] || 0) + 1;

        // Calculate average rating
        if (item.rating && typeof item.rating === "number") {
            ratingSum += item.rating;
            ratingCount++;
        }

        // Track date range
        if (item.date) {
            if (
                !stats.dateRange.earliest ||
                item.date < stats.dateRange.earliest
            ) {
                stats.dateRange.earliest = item.date;
            }
            if (!stats.dateRange.latest || item.date > stats.dateRange.latest) {
                stats.dateRange.latest = item.date;
            }
        }
    }

    stats.averageRating = ratingCount > 0 ? ratingSum / ratingCount : 0;

    return stats;
}
